import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import HeritageMark from "@/components/common/HeritageMark";

const CHOICES = [
  {
    key: "holdings",
    to: "/acquisitions",
    eyebrow: "Acquisitions",
    name: "Heritage Homecare Holdings Group",
    detail: "LLC",
    tagline:
      "We acquire healthcare businesses at 3–5× earnings — with financing already secured.",
    image:
      "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?auto=format&fit=crop&w=1200&q=85",
    alt: "Team of hands joined in agreement",
  },
  {
    key: "nj",
    to: "/nj",
    eyebrow: "Hoboken · New Jersey",
    name: "Heritage Homecare",
    detail: "NJ",
    tagline:
      "Clinical-grade home care, delivered with the warmth of family.",
    image:
      "https://images.pexels.com/photos/7551598/pexels-photo-7551598.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Older gentleman reading a tablet in a sunlit home",
  },
  {
    key: "ny",
    to: "/ny",
    eyebrow: "Midtown · New York",
    name: "Heritage Companion Care",
    detail: "NY",
    tagline:
      "Thoughtful, non-medical companionship so loved ones stay independent at home.",
    image:
      "https://images.pexels.com/photos/7551656/pexels-photo-7551656.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Companion visiting an elderly man by a window",
  },
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.0,
      delay: 0.1 + i * 0.12,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const card = {
  hidden: { opacity: 0, y: 36 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      delay: 0.55 + i * 0.1,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export default function ChooserPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Heritage · Homecare Holdings Group";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <main
      data-testid="chooser-page"
      className="relative min-h-screen bg-[#FDFBF7] text-[#1A2621] font-sans-body overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-[#E6EAE3] blur-3xl opacity-70" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#F3EFE9] blur-3xl opacity-70" />

      {/* Top brand bar */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HeritageMark size={40} />
          <div className="font-serif-display leading-none">
            <div className="text-xl md:text-2xl text-[#1A2621]">Heritage</div>
            <div className="text-[11px] uppercase tracking-[0.26em] text-[#4A5D54] mt-1">
              Homecare Holdings Group LLC
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/careers"
            data-testid="chooser-careers-link"
            className="text-sm text-[#4A5D54] hover:text-[#C86A51] transition-colors"
          >
            Careers
          </Link>
          <a
            href="mailto:info@heracq.com"
            data-testid="chooser-email"
            className="inline-flex items-center gap-2 text-sm text-[#4A5D54] hover:text-[#C86A51] transition-colors"
          >
            info@heracq.com
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>

      {/* Headline */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-10 md:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="eyebrow"
          data-testid="chooser-eyebrow"
        >
          The Heritage Family
        </motion.div>

        <h1
          data-testid="chooser-headline"
          className="font-serif-display mt-6 text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.98] tracking-tight text-[#1A2621] max-w-4xl"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              variants={line}
              initial="hidden"
              animate="show"
              custom={0}
              className="block"
            >
              Three ways to work
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              variants={line}
              initial="hidden"
              animate="show"
              custom={1}
              className="block italic text-[#284A3B]"
            >
              with Heritage.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-lg md:text-xl text-[#4A5D54] leading-relaxed"
        >
          Choose a destination — our acquisitions arm, our New Jersey homecare
          service, or our New York companion care practice.
        </motion.p>
      </section>

      {/* Choice cards */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {CHOICES.map((c, i) => (
            <motion.div
              key={c.key}
              variants={card}
              initial="hidden"
              animate="show"
              custom={i}
            >
              <Link
                to={c.to}
                data-testid={`chooser-card-${c.key}`}
                className="group relative block rounded-3xl overflow-hidden border border-[#D5DACF] bg-white aspect-[3/4] md:aspect-auto md:min-h-[560px] shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
              >
                {/* Image */}
                <img
                  src={c.image}
                  alt={c.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.07]"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2621]/90 via-[#1A2621]/40 to-[#1A2621]/10" />
                {/* Spotlight on hover */}
                <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C86A51]/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-8 text-[#FDFBF7]">
                  <div className="flex items-start justify-between">
                    <div className="text-[11px] uppercase tracking-[0.26em] text-[#FDFBF7]/85">
                      {c.eyebrow}
                    </div>
                    <div className="w-10 h-10 rounded-full border border-[#FDFBF7]/40 flex items-center justify-center transition-all duration-500 group-hover:border-[#C86A51] group-hover:bg-[#C86A51]">
                      <ArrowUpRight
                        size={16}
                        className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="font-serif-display text-[clamp(2rem,3.4vw,3rem)] leading-[1] tracking-tight">
                      {c.name}
                    </div>
                    {c.detail && (
                      <div className="font-serif-display italic text-xl text-[#FDFBF7]/85 mt-2">
                        {c.detail}
                      </div>
                    )}
                    <p className="mt-5 text-sm md:text-base text-[#FDFBF7]/85 leading-relaxed max-w-md">
                      {c.tagline}
                    </p>

                    <div className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b border-[#FDFBF7]/40 pb-1 w-fit group-hover:border-[#C86A51] group-hover:text-[#C86A51] transition-colors">
                      Enter site
                      <ArrowUpRight size={14} />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Footer line */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-[#4A5D54]">
        <div>
          © {new Date().getFullYear()} Heritage Homecare Holdings Group LLC.
          All rights reserved.
        </div>
        <div className="font-serif-display italic text-[#284A3B]">
          Compassion · Dignity · Reliability
        </div>
      </div>
    </main>
  );
}
